import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faEnvelope, faPhone, faMobile, faEdit, faSave, faTimes, faShieldAlt, faKey, faEye, faEyeSlash, faTrash } from '@fortawesome/free-solid-svg-icons';
import useProfile from '../hooks/useProfile';
import { UI_COLORS, GRADIENTS, colorHelpers } from '../utils/colors';
import { useMessageAlert } from '../components/common/MessageAlert';
import { validatePasswordStrength } from '../utils/validate';
import { useRole } from '../context/roleContext';
import { USER_ROLES } from '../utils/constants';
import ordersService from '../services/ordersService';
import SuperAdminDeleteDialog from '../components/common/SuperAdminDeleteDialog';
import { formatBulkDeleteMessage } from '../utils/format';
import { useTranslation } from 'react-i18next';
import { setLocaleFromUserLanguage } from '../i18n';


/**
 * ProfilePage - User profile management with editable fields
 */
const ProfilePage = () => {
  const { t } = useTranslation();
  const { profile, loading, error, fetchProfile, updateProfile, resetPassword, clearError } = useProfile();
  const { showError, showSuccess } = useMessageAlert();
  const { userRole, checkDeleteAccess } = useRole();
  
  const [isEditing, setIsEditing] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone_number: '',
    mobile_number: '',
  });
  const [passwordData, setPasswordData] = useState({
    old_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    confirm_password: false,
  });
  const [formErrors, setFormErrors] = useState({});
  const [passwordErrors, setPasswordErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [showSuperDeleteModal, setShowSuperDeleteModal] = useState(false);
  const [superDeleteLoading, setSuperDeleteLoading] = useState(false);

  const handleSuperDelete = async (password) => {
    setSuperDeleteLoading(true);
    try {
      const payload = { password };
      const result = await ordersService.bulkDeleteAllOrdersGlobal(payload);

      if (result.success) {
        showSuccess(formatBulkDeleteMessage(result, t('profile.superDelete.successMessage')));
        setShowSuperDeleteModal(false);
      } else {
        showError(result.error || t('profile.superDelete.failureMessage'));
      }
    } catch (err) {
      console.error(err);
      showError(err.message || t('profile.superDelete.failureMessage'));
    } finally {
      setSuperDeleteLoading(false);
    }
  };

  // Fetch profile on mount
  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Populate form when profile loads
  useEffect(() => {
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
        mobile_number: profile.mobile_number || '',
      });
    }
  }, [profile]);

  // Show transient error messages via the MessageAlert provider
  useEffect(() => {
    if (error) {
      try {
        showError(error);
      } catch (e) {
        // If MessageAlert provider is not mounted, fall back to console
        // (do not throw from UI)
        // eslint-disable-next-line no-console
        console.error('MessageAlert provider missing:', e);
      }
      // Clear the profile error state after showing the toast to avoid
      // repeatedly showing the same message on re-renders.
      clearError();
    }
  }, [error, showError, clearError]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user types
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name || formData.first_name.trim().length < 2) {
      errors.first_name = t('profile.validation.firstNameMin');
    }
    
    if (!formData.last_name || formData.last_name.trim().length < 2) {
      errors.last_name = t('profile.validation.lastNameMin');
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      errors.email = t('profile.validation.emailInvalid');
    }
    
    // Phone number validation (10 digits)
    const phoneRegex = /^\d{10}$/;
    if (formData.phone_number && !phoneRegex.test(formData.phone_number)) {
      errors.phone_number = t('profile.validation.phoneInvalid');
    }
    
    if (formData.mobile_number && !phoneRegex.test(formData.mobile_number)) {
      errors.mobile_number = t('profile.validation.mobileInvalid');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    setSaving(true);
    const result = await updateProfile(formData);
    setSaving(false);
    
    if (result.success) {
      setIsEditing(false);
    }
  };

  // Change UI language: switch immediately and persist to the backend (PUT /users/me).
  const handleLanguageChange = async (language) => {
    setLocaleFromUserLanguage(language); // instant switch + localStorage
    const result = await updateProfile({ language });
    if (result?.success) {
      showSuccess(t('profile.language.updated'));
    } else if (result?.error) {
      showError(result.error);
    }
  };

  const handleCancel = () => {
    // Reset form to profile data
    if (profile) {
      setFormData({
        first_name: profile.first_name || '',
        last_name: profile.last_name || '',
        email: profile.email || '',
        phone_number: profile.phone_number || '',
        mobile_number: profile.mobile_number || '',
      });
    }
    setFormErrors({});
    setIsEditing(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    // Clear field error when user types
    if (passwordErrors[name]) {
      setPasswordErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const validatePasswordForm = () => {
    const errors = {};
    
    if (!passwordData.old_password) {
      errors.old_password = t('profile.resetPassword.errors.currentPasswordRequired');
    }
    
    if (!passwordData.new_password) {
      errors.new_password = t('profile.resetPassword.errors.newPasswordRequired');
    } else {
      const passwordValidation = validatePasswordStrength(passwordData.new_password);
      if (!passwordValidation.isValid) {
        if (passwordValidation.failed.includes('length')) {
          errors.new_password = t('profile.resetPassword.errors.passwordTooShort');
        } else if (passwordValidation.failed.includes('lowercase')) {
          errors.new_password = t('profile.resetPassword.errors.passwordMissingLowercase');
        } else if (passwordValidation.failed.includes('uppercase')) {
          errors.new_password = t('profile.resetPassword.errors.passwordMissingUppercase');
        } else if (passwordValidation.failed.includes('number')) {
          errors.new_password = t('profile.resetPassword.errors.passwordMissingNumber');
        } else if (passwordValidation.failed.includes('symbol')) {
          errors.new_password = t('profile.resetPassword.errors.passwordMissingSymbol');
        }
      }
    }
    
    if (!passwordData.confirm_password) {
      errors.confirm_password = t('profile.resetPassword.errors.confirmPasswordRequired');
    } else if (passwordData.new_password !== passwordData.confirm_password) {
      errors.confirm_password = t('profile.resetPassword.errors.confirmPasswordMismatch');
    }

    setPasswordErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleResetPassword = async () => {
    if (!validatePasswordForm()) return;
    
    setResettingPassword(true);
    const result = await resetPassword(passwordData);
    setResettingPassword(false);
    
    if (result.success) {
      showSuccess(t('profile.resetPassword.notifications.success'));
      setShowResetPassword(false);
      setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
      setPasswordErrors({});
    } else {
      showError(result.error);
    }
  };

  const handleCancelPasswordReset = () => {
    setPasswordData({ old_password: '', new_password: '', confirm_password: '' });
    setPasswordErrors({});
    setShowResetPassword(false);
  };

  const renderField = (label, name, value, icon, readOnly = false) => {
    const hasError = formErrors[name];
    
    return (
      <div className="mb-6">
        <label 
          htmlFor={name}
          className="block text-sm font-semibold mb-2"
          style={{ color: UI_COLORS.TEXT.PRIMARY }}
        >
          <FontAwesomeIcon icon={icon} className="mr-2" style={{ color: UI_COLORS.BRAND.PRIMARY }} />
          {label}
        </label>
        <input
          id={name}
          name={name}
          type="text"
          value={value}
          onChange={handleInputChange}
          disabled={!isEditing || readOnly}
          className="w-full px-4 py-3 rounded-lg border-2 transition-all duration-200 focus:outline-none"
          style={{
            backgroundColor: isEditing && !readOnly ? UI_COLORS.BACKGROUNDS.CARD : colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.05),
            borderColor: hasError 
              ? UI_COLORS.STATUS.ERROR 
              : isEditing && !readOnly 
                ? UI_COLORS.BRAND.PRIMARY 
                : colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.2),
            color: readOnly ? colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.6) : UI_COLORS.TEXT.PRIMARY,
            cursor: readOnly ? 'not-allowed' : isEditing ? 'text' : 'default',
          }}
        />
        {hasError && (
          <p className="mt-1 text-sm" style={{ color: UI_COLORS.STATUS.ERROR }}>
            {hasError}
          </p>
        )}
      </div>
    );
  };

  if (loading && !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div 
            className="inline-block animate-spin rounded-full h-12 w-12 border-b-2"
            style={{ borderColor: UI_COLORS.BRAND.PRIMARY }}
          />
          <p className="mt-4 text-lg" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
            {t('profile.loading')}
          </p>
        </div>
      </div>
    );
  }

  const userTypeLabel = profile?.user_type 
    ? (t('users.forms.userTypes', { returnObjects: true })?.find(ut => ut.value === profile.user_type)?.label || profile.user_type.replace('_', ' '))
    : '';

  return (
    <div 
      className="min-h-screen p-6 flex items-start justify-center"
      style={{ 
        backgroundColor: UI_COLORS.BACKGROUNDS.PRIMARY,
        overflowY: 'hidden',
        maxHeight: '100vh',
        overscrollBehavior: 'none'
      }}
    >
      <div style={{ transform: 'scale(0.90)', transformOrigin: 'top center', width: '100%' }}>
      {/* Header */}
      <div className="mb-8">
        <h1 
          className="text-3xl font-bold mb-2"
          style={{ color: UI_COLORS.TEXT.PRIMARY }}
        >
          {t('profile.title')}
        </h1>
        <p style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          {t('profile.subtitle')}
        </p>
      </div>

      {/* Error -> show as transient message alert */}
      {/* showError will be triggered in an effect below when `error` changes */}

      {/* Profile Card */}
      <div 
        className="max-w-4xl mx-auto rounded-2xl shadow-2xl overflow-hidden"
        style={{ 
          background: UI_COLORS.BACKGROUNDS.CARD,
          boxShadow: `0 20px 60px ${UI_COLORS.SHADOWS.STRONG}` 
        }}
      >
        {/* Card Header */}
        <div 
          className="p-8 border-b"
          style={{ 
            background: GRADIENTS.PRIMARY,
            borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.WHITE, 0.2)
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Avatar */}
              <div 
                className="w-20 h-20 rounded-full flex items-center justify-center text-2xl font-bold shadow-lg"
                style={{ 
                  background: GRADIENTS.SECONDARY,
                  color: UI_COLORS.TEXT.WHITE
                }}
              >
                {profile?.first_name ? profile.first_name.charAt(0).toUpperCase() : 'U'}
              </div>
              
              <div>
                <h2 
                  className="text-2xl font-bold"
                  style={{ color: UI_COLORS.TEXT.SECONDARY }}
                >
                  {profile?.first_name} {profile?.last_name}
                </h2>
                <div 
                  className="flex items-center mt-1 px-3 py-1 rounded-full inline-block"
                  style={{ 
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.SUCCESS, 0.2),
                    color: UI_COLORS.STATUS.SUCCESS
                  }}
                >
                  <FontAwesomeIcon icon={faShieldAlt} className="mr-2" />
                  <span className="text-sm font-semibold">{userTypeLabel}</span>
                </div>
              </div>
            </div>

            {/* Edit Button */}
            {!isEditing && (
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowResetPassword(true)}
                  className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                  style={{ 
                    backgroundColor: colorHelpers.withOpacity(UI_COLORS.STATUS.WARNING, 0.9),
                    color: UI_COLORS.TEXT.WHITE
                  }}
                >
                  <FontAwesomeIcon icon={faKey} className="mr-2" />
                    {t('profile.resetPassword.buttons.reset')}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                  style={{ 
                    background: GRADIENTS.SECONDARY,
                    color: UI_COLORS.TEXT.WHITE
                  }}
                >
                  <FontAwesomeIcon icon={faEdit} className="mr-2" />
                  {t('profile.buttons.edit')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Card Body */}
        <div 
          className="p-8"
          style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* First Name */}
            <div className="md:col-span-1">
              {renderField(t('profile.fields.firstName'), 'first_name', formData.first_name, faUser)}
            </div>

            {/* Last Name */}
            <div className="md:col-span-1">
              {renderField(t('profile.fields.lastName'), 'last_name', formData.last_name, faUser)}
            </div>

            {/* Email */}
            <div className="md:col-span-2">
              {renderField(t('profile.fields.email'), 'email', formData.email, faEnvelope)}
            </div>

            {/* Phone Number */}
            <div className="md:col-span-1">
              {renderField(t('profile.fields.phoneNumber'), 'phone_number', formData.phone_number, faPhone)}
            </div>

            {/* Mobile Number */}
            <div className="md:col-span-1">
              {renderField(t('profile.fields.mobileNumber'), 'mobile_number', formData.mobile_number, faMobile)}
            </div>

            {/* User Type (Read-only) */}
            <div className="md:col-span-2">
              {renderField(t('profile.fields.userType'), 'user_type', userTypeLabel, faShieldAlt, true)}
            </div>

            {/* Language selector — switches UI language immediately and persists */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold mb-2" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
                {t('profile.language.label')}
              </label>
              <select
                value={profile?.language === 'hindi' ? 'hindi' : 'english'}
                onChange={(e) => handleLanguageChange(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border-2 outline-none transition-all"
                style={{ borderColor: UI_COLORS.FORMS.INPUT.border, backgroundColor: UI_COLORS.FORMS.INPUT.background, color: UI_COLORS.FORMS.INPUT.text }}
              >
                <option value="english">{t('profile.language.english')}</option>
                <option value="hindi">{t('profile.language.hindi')}</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          {isEditing && (
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t" style={{ borderColor: colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.1) }}>
              <button
                onClick={handleCancel}
                disabled={saving}
                className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                style={{ 
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.1),
                  color: UI_COLORS.TEXT.PRIMARY,
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                {t('profile.buttons.cancel')}
              </button>
              
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                style={{ 
                  background: GRADIENTS.PRIMARY,
                  color: UI_COLORS.TEXT.SECONDARY,
                  opacity: saving ? 0.6 : 1,
                  cursor: saving ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faSave} className="mr-2" />
                {saving ? t('profile.buttons.saving') : t('profile.buttons.save')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Additional Info Card */}
      <div 
        className="max-w-4xl mx-auto mt-6 p-6 rounded-xl"
        style={{ 
          backgroundColor: colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.05),
          border: `1px solid ${colorHelpers.withOpacity(UI_COLORS.BRAND.PRIMARY, 0.2)}`
        }}
      >
        <p className="text-sm" style={{ color: UI_COLORS.TEXT.SECONDARY }}>
          <strong style={{ color: UI_COLORS.TEXT.PRIMARY }}>Note:</strong> {t('profile.note')}
        </p>
      </div>
      </div>

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          onClick={handleCancelPasswordReset}
        >
          <div 
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
            style={{ backgroundColor: UI_COLORS.BACKGROUNDS.CARD }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 
                className="text-xl font-bold"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                <FontAwesomeIcon icon={faKey} className="mr-2" style={{ color: UI_COLORS.BRAND.PRIMARY }} />
                {t('profile.resetPassword.title')}
              </h3>
              <button
                onClick={handleCancelPasswordReset}
                className="p-2 rounded-lg transition-all duration-200 hover:scale-105"
                style={{ 
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.1),
                  color: UI_COLORS.TEXT.PRIMARY
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
                <span className="ml-2">{t('profile.resetPassword.buttons.cancel')}</span>
              </button>
            </div>

            {/* Old Password */}
            <div className="mb-4">
              <label 
                className="block text-sm font-semibold mb-2"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                {t('profile.resetPassword.fields.currentPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPasswords.old_password ? "text" : "password"}
                  name="old_password"
                  value={passwordData.old_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 transition-all duration-200 focus:outline-none"
                  style={{
                    backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                    borderColor: passwordErrors.old_password 
                      ? UI_COLORS.STATUS.ERROR 
                      : UI_COLORS.BRAND.PRIMARY,
                    color: UI_COLORS.TEXT.PRIMARY,
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('old_password')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                  style={{ color: UI_COLORS.TEXT.SECONDARY }}
                >
                  <FontAwesomeIcon icon={showPasswords.old_password ? faEyeSlash : faEye} />
                </button>
              </div>
              {passwordErrors.old_password && (
                <p className="mt-1 text-sm" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {passwordErrors.old_password}
                </p>
              )}
            </div>

            {/* New Password */}
            <div className="mb-4">
              <label 
                className="block text-sm font-semibold mb-2"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                {t('profile.resetPassword.fields.newPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPasswords.new_password ? "text" : "password"}
                  name="new_password"
                  value={passwordData.new_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 transition-all duration-200 focus:outline-none"
                  style={{
                    backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                    borderColor: passwordErrors.new_password 
                      ? UI_COLORS.STATUS.ERROR 
                      : UI_COLORS.BRAND.PRIMARY,
                    color: UI_COLORS.TEXT.PRIMARY,
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('new_password')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                  style={{ color: UI_COLORS.TEXT.SECONDARY }}
                >
                  <FontAwesomeIcon icon={showPasswords.new_password ? faEyeSlash : faEye} />
                </button>
              </div>
              {passwordErrors.new_password && (
                <p className="mt-1 text-sm" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {passwordErrors.new_password}
                </p>
              )}
            </div>

            {/* Confirm Password */}
            <div className="mb-6">
              <label 
                className="block text-sm font-semibold mb-2"
                style={{ color: UI_COLORS.TEXT.PRIMARY }}
              >
                {t('profile.resetPassword.fields.confirmPassword')}
              </label>
              <div className="relative">
                <input
                  type={showPasswords.confirm_password ? "text" : "password"}
                  name="confirm_password"
                  value={passwordData.confirm_password}
                  onChange={handlePasswordChange}
                  className="w-full px-4 py-3 pr-12 rounded-lg border-2 transition-all duration-200 focus:outline-none"
                  style={{
                    backgroundColor: UI_COLORS.BACKGROUNDS.CARD,
                    borderColor: passwordErrors.confirm_password 
                      ? UI_COLORS.STATUS.ERROR 
                      : UI_COLORS.BRAND.PRIMARY,
                    color: UI_COLORS.TEXT.PRIMARY,
                  }}
                />
                <button
                  type="button"
                  onClick={() => togglePasswordVisibility('confirm_password')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1"
                  style={{ color: UI_COLORS.TEXT.SECONDARY }}
                >
                  <FontAwesomeIcon icon={showPasswords.confirm_password ? faEyeSlash : faEye} />
                </button>
              </div>
              {passwordErrors.confirm_password && (
                <p className="mt-1 text-sm" style={{ color: UI_COLORS.STATUS.ERROR }}>
                  {passwordErrors.confirm_password}
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={handleCancelPasswordReset}
                disabled={resettingPassword}
                className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105"
                style={{ 
                  backgroundColor: colorHelpers.withOpacity(UI_COLORS.TEXT.PRIMARY, 0.1),
                  color: UI_COLORS.TEXT.PRIMARY,
                  opacity: resettingPassword ? 0.6 : 1,
                  cursor: resettingPassword ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faTimes} className="mr-2" />
                Cancel
              </button>
              
              <button
                onClick={handleResetPassword}
                disabled={resettingPassword}
                className="px-6 py-3 rounded-lg font-semibold transition-all duration-200 hover:scale-105 shadow-lg"
                style={{ 
                  background: GRADIENTS.PRIMARY,
                  color: UI_COLORS.TEXT.SECONDARY,
                  opacity: resettingPassword ? 0.6 : 1,
                  cursor: resettingPassword ? 'not-allowed' : 'pointer'
                }}
              >
                <FontAwesomeIcon icon={faKey} className="mr-2" />
                {resettingPassword ? t('profile.resetPassword.buttons.resetting') : t('profile.resetPassword.buttons.reset')}
              </button>
            </div>
          </div>
        </div>
      )}

      {userRole === USER_ROLES.SUPER_ADMIN && checkDeleteAccess() && (
        <div className="mt-8 border-t border-red-200 pt-6">
          <h3 className="text-xl font-bold text-red-600 mb-2">Danger Zone</h3>
          <p className="text-sm text-gray-600 mb-4">
            These actions are highly destructive and can only be performed by Super Admins.
          </p>
          <button
            onClick={() => setShowSuperDeleteModal(true)}
            className="px-3 py-1.5 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition flex items-center shadow text-xs"
          >
            <FontAwesomeIcon icon={faTrash} className="mr-2" />
            Super Delete All Records (Orders & POs)
          </button>
        </div>
      )}

      {showSuperDeleteModal && (
        <SuperAdminDeleteDialog
          isOpen={showSuperDeleteModal}
          typeLabel="Orders and Purchase Orders"
          loading={superDeleteLoading}
          onConfirm={handleSuperDelete}
          onCancel={() => setShowSuperDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default ProfilePage;
